import React, { useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN, internalUrl } from './siteConfig';
import { DEFAULT_PAGE_TITLE } from './seoDefaults';

const PATH = '/reading-time-tool';
const CANONICAL = `${SITE_ORIGIN}${PATH}`;
const WPM = 200;

function stripHtml(s) {
  return s.replace(/<[^>]*>/g, ' ');
}

function countWords(s) {
  const t = stripHtml(s).trim();
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}

function analyzeHeadline(title) {
  const t = stripHtml(title).trim();
  if (!t) {
    return { empty: true, length: 0, tooLong: false, allCaps: false, message: 'Add a headline to see checks.' };
  }
  const len = t.length;
  const letters = t.replace(/[^A-Za-z]/g, '');
  const allCaps = letters.length > 2 && t === t.toUpperCase() && /[A-Z]/.test(t);
  const tooLong = len > 70;
  return {
    empty: false,
    length: len,
    tooLong,
    allCaps,
    message: [tooLong ? 'Very long for many layouts — consider shortening for search results and banners.' : null, allCaps ? 'All caps can read as shouty — sentence or title case often works better.' : null]
      .filter(Boolean)
      .join(' ') || 'Looks reasonable for a primary headline.'
  };
}

export default function ReadingTimeToolPage() {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const bodyId = `${baseId}-body`;
  const resultsId = `${baseId}-results`;

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    document.title = 'Reading time & headline helper — free content tool | WebCodeCraft';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', CANONICAL);
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute('content') : '';
    if (desc) {
      desc.setAttribute(
        'content',
        'Free browser-only tool: paste headline and body for word count, estimated reading time, and simple headline checks. No data leaves your device. WebCodeCraft, UK.'
      );
    }
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Reading time and headline helper',
      url: CANONICAL,
      description: 'Estimate reading time and review headline length — client-side only.',
      isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN }
    });
    ld.setAttribute('data-page', 'reading-time-tool');
    document.head.appendChild(ld);
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      if (canonical) canonical.setAttribute('href', `${SITE_ORIGIN}/`);
      if (desc) desc.setAttribute('content', prevDesc || '');
      ld.remove();
    };
  }, []);

  const headlineInfo = useMemo(() => analyzeHeadline(title), [title]);
  const bodyWords = useMemo(() => countWords(body), [body]);
  const titleWords = useMemo(() => countWords(title), [title]);
  const totalWords = bodyWords + (title ? titleWords : 0);
  const minutes = totalWords <= 0 ? 0 : Math.max(1, Math.ceil(totalWords / WPM));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header />
      <CookieBanner />
      <main id="main-content" className="pt-20 min-h-screen min-w-0 overflow-x-clip">
        <section className="container mx-auto px-4 py-10 max-w-3xl" aria-labelledby="rt-heading">
          <m.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="text-sm font-medium text-indigo-600 mb-2">
              <Link to="/check-your-seo" className="underline underline-offset-2 hover:text-indigo-800">
                Tools
              </Link>
              <span className="mx-1 text-slate-400" aria-hidden="true">
                /
              </span>
              <span className="text-slate-700">Reading time</span>
            </p>
            <h1 id="rt-heading" className="text-3xl sm:text-4xl font-bold text-slate-900 font-display tracking-tight mb-4">
              Reading time & headline helper
            </h1>
            <p className="text-slate-600 leading-relaxed mb-2">
              Paste a headline and body below. Everything runs in your browser — WebCodeCraft does not receive your text.
              Reading speed is estimated at <strong>{WPM} words per minute</strong> (adjust mentally for dense or technical copy).
            </p>
          </m.div>

          <div className="mt-8 space-y-6">
            <div>
              <label htmlFor={titleId} className="block text-sm font-semibold text-slate-800 mb-1.5">
                Headline (optional)
              </label>
              <input
                id={titleId}
                type="text"
                className="form-input w-full"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoComplete="off"
                placeholder="e.g. Affordable web design in Devon"
              />
            </div>
            <div>
              <label htmlFor={bodyId} className="block text-sm font-semibold text-slate-800 mb-1.5">
                Body
              </label>
              <textarea
                id={bodyId}
                className="form-textarea w-full min-h-[200px]"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Paste paragraphs or HTML — tags are stripped for counting only."
                aria-describedby={`${baseId}-hint`}
              />
              <p id={`${baseId}-hint`} className="text-xs text-slate-500 mt-1.5">
                HTML tags are removed for counting; they are not rendered as live preview.
              </p>
            </div>
          </div>

          <section
            id={resultsId}
            className="mt-10 bg-white rounded-xl border border-slate-200 shadow-sm p-5 sm:p-6"
            aria-live="polite"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-4">Results</h2>
            <dl className="space-y-3 text-sm sm:text-base">
              <div className="flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-3">
                <dt className="font-semibold text-slate-800">Word count (body)</dt>
                <dd className="text-slate-700 tabular-nums">{bodyWords}</dd>
              </div>
              <div className="flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-3">
                <dt className="font-semibold text-slate-800">Est. reading time</dt>
                <dd className="text-slate-700">
                  {totalWords === 0 ? '—' : `${minutes} min (${totalWords} words @ ${WPM} wpm)`}
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-slate-800 mb-1">Headline</dt>
                <dd className="text-slate-700">{headlineInfo.message}</dd>
                {!headlineInfo.empty && (
                  <p className="text-slate-600 text-sm mt-2">
                    Length: {headlineInfo.length} characters
                    {headlineInfo.tooLong ? ' — consider under ~60–70 for many SERP titles.' : ''}
                  </p>
                )}
              </div>
            </dl>
          </section>

          <p className="mt-10 text-sm text-slate-600">
            Need help shaping copy for your site?{' '}
            <a href={internalUrl('contact')} className="text-indigo-600 font-medium underline underline-offset-2">
              Contact WebCodeCraft
            </a>
            .
          </p>
        </section>
        <Footer />
      </main>
    </div>
  );
}
